import React from 'react';
import { MapPin, ExternalLink, Building2, ArrowLeft } from 'lucide-react';
import { useData } from '../context/DataContext';

const PartnersSection = ({ setActiveTab }) => {
  const { partners, loading } = useData();

  if (loading) {
    return (
      <section className="min-h-[calc(100vh-80px)] py-12 bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-[#1e3a5f] border-t-transparent rounded-full"></div>
      </section>
    );
  }

  return (
    <section className="min-h-[calc(100vh-80px)] py-12 bg-[#f8fafc]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <button
          onClick={() => setActiveTab('home')}
          className="flex items-center gap-2 text-[#3d6a99] hover:text-[#1e3a5f] mb-8 transition-colors duration-300"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a5f]/10 text-[#1e3a5f] rounded-full text-sm font-medium mb-4">
            Our Partnership
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e3a5f] mb-6">
            Partner Schools
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Dedicated schools working together across borders to create peaceful learning environments.
          </p>
        </div>

        {/* Partners Grid */}
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="group bg-white rounded-3xl p-8 border border-[#e2e8f0] shadow-sm hover:shadow-xl transition-all duration-500 hover:-translate-y-1"
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-4">
                  <span className="text-5xl">{partner.flag}</span>
                  <div>
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      partner.role === 'Coordinator'
                        ? 'bg-[#1e3a5f] text-white'
                        : 'bg-[#e2e8f0] text-[#1e3a5f]'
                    }`}>
                      {partner.role}
                    </span>
                    <p className="text-[#3d6a99] font-medium mt-1">{partner.country}</p>
                  </div>
                </div>
              </div>

              {/* School Name */}
              <h3 className="text-xl font-bold text-[#1e3a5f] mb-4 group-hover:text-[#2c5282] transition-colors duration-300">
                {partner.name}
              </h3>

              {/* Location */}
              <div className="flex items-center gap-2 text-gray-500 mb-4">
                <MapPin className="w-4 h-4" />
                <span className="text-sm">{partner.city}</span>
              </div>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed mb-6">
                {partner.description}
              </p>

              {/* Website Link */}
              {partner.website && partner.website !== '#' && (
                <a
                  href={partner.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#2c5282] hover:text-[#1e3a5f] font-medium transition-colors duration-300"
                >
                  <ExternalLink className="w-4 h-4" />
                  Visit Website
                </a>
              )}
            </div>
          ))}
        </div>

        {/* Partnership Visual */}
        {partners.length >= 2 && (
          <div className="mt-16 flex items-center justify-center">
            <div className="flex items-center gap-4 sm:gap-8">
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mb-2 mx-auto">
                  <span className="text-3xl sm:text-4xl">{partners[0]?.flag}</span>
                </div>
                <p className="text-sm text-gray-600">{partners[0]?.country}</p>
              </div>
              
              <div className="flex items-center">
                <div className="w-12 sm:w-24 h-0.5 bg-gradient-to-r from-[#1e3a5f] to-[#3d6a99]" />
                <Building2 className="w-8 h-8 sm:w-10 sm:h-10 text-[#2c5282] mx-2" />
                <div className="w-12 sm:w-24 h-0.5 bg-gradient-to-r from-[#3d6a99] to-[#1e3a5f]" />
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-[#1e3a5f] rounded-2xl flex items-center justify-center mb-2 mx-auto">
                  <span className="text-3xl sm:text-4xl">{partners[1]?.flag}</span>
                </div>
                <p className="text-sm text-gray-600">{partners[1]?.country}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PartnersSection;
